import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublicKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabasePublicKey) {
  throw new Error(
    'Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY (or VITE_SUPABASE_ANON_KEY).'
  );
}

export const supabase = createClient(supabaseUrl, supabasePublicKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

export interface Database {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string;
          user_id: string;
          name: string;
          email: string;
          plan: 'free' | 'basic' | 'premium' | 'revelation';
          timezone: string;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['user_profiles']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['user_profiles']['Insert']>;
      };
      journal_entries: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          content: string;
          mood?: string;
          tags: string[];
          is_pinned: boolean;
          is_starred: boolean;
          is_archived: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['journal_entries']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['journal_entries']['Insert']>;
      };
      mindshifts: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          content: string;
          category?: 'belief' | 'emotional' | 'self-concept' | 'universal';
          tags: string[];
          is_pinned: boolean;
          is_starred: boolean;
          related_journal_id?: string;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['mindshifts']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['mindshifts']['Insert']>;
      };
      actions: {
        Row: {
          id: string;
          user_id: string;
          content: string;
          description?: string;
          date: string;
          due_date?: string;
          completed: boolean;
          completed_date?: string;
          is_pinned: boolean;
          is_recurring: boolean;
          recurring_frequency?: 'daily' | 'weekly' | 'monthly';
          tags: string[];
          from_mindshift_id?: string;
          related_entry_id?: string;
          reflection?: string;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['actions']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['actions']['Insert']>;
      };
      daily_moods: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          mood: 'positive' | 'neutral' | 'challenging';
          energy: number;
          stress: number;
          gratitude: number;
          clarity: number;
          connection: number;
          notes?: string;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['daily_moods']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['daily_moods']['Insert']>;
      };
      notes: {
        Row: {
          id: string;
          user_id: string;
          content: string;
          pinned: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['notes']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['notes']['Insert']>;
      };
      saved_insights: {
        Row: {
          id: string;
          user_id: string;
          type: 'ask' | 'quick' | 'deepDive';
          content: string;
          question?: string;
          quick_type?: 'prompt' | 'reframe' | 'action' | 'metaphor';
          tags: string[];
          date: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          user_id: string;
          type: 'ask' | 'quick' | 'deepDive';
          content: string;
          question?: string | null;
          quick_type?: 'prompt' | 'reframe' | 'action' | 'metaphor' | null;
          tags?: string[];
          date?: string;
          created_at?: string;
        };
        Update: Partial<Database['public']['Tables']['saved_insights']['Insert']> & {
          updated_at?: string;
        };
      };
      user_ai_usage: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          ask_count: number;
          quick_prompt_count: number;
          quick_reframe_count: number;
          quick_action_count: number;
          quick_metaphor_count: number;
          deep_dive_used: boolean;
          deep_dive_rerolls_used: number;
          free_welcome_pack_claimed: boolean;
          free_welcome_pack_remaining: {
            deepDive: number;
            ask: number;
            quick: {
              prompt: number;
              reframe: number;
              action: number;
              metaphor: number;
            };
          };
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['user_ai_usage']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['user_ai_usage']['Insert']>;
      };
    };
  };
}
